import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import allLocales from '@fullcalendar/core/locales-all';
import type { DateSelectArg, EventClickArg } from '@fullcalendar/core';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import type { SpaceWeeklySchedule } from '../../models/Space';
import type { DiscountSettings } from '../../models/Organization';
import type {
  CreateHostTutorBookingRequest,
  CreateManualTutorBookingResponse,
  CreateTutorCalendarEventRequest,
  TutorLessonOption,
} from '../../models/Tutor';
import type { HostBookingClientOption } from '../../models/HostOrganizationUser';
import { sharedCalendarCompactStyles } from '../../styles/sharedCalendarStyles';
import { calendarOverlayCompactStyles } from '../../styles/calendarOverlayStyles';
import { tutorCalendarCompactStyles, tutorCalendarStyles } from '../../styles/tutorCalendarStyles';
import { useThemeContext } from '../../contexts/ThemeContext';
import {
  buildBlockedSlotOverlayEvents,
  buildDayOffOverlayEvents,
  buildPastBlockedOverlayEvents,
  buildWorkingHoursOverlayEvents,
  calculateScheduleTimeRange,
  mountPastEventClass,
  withPastEventClassNames,
} from '../../utils/calendarScheduleOverlays';
import {
  TUTOR_CALENDAR_COLORS,
  TutorCalendarLessonEvent,
} from '../../utils/tutorCalendarEvents';
import {
  buildHostTutorWalkInBookingRequest,
  validateHostTutorWalkInBooking,
} from '../../utils/hostTutorWalkInBooking';
import { bookingSessionsOverlap, buildTutorBookingOccurrences } from '../../utils/bookingRepeatSlots';
import {
  buildManualGuestBookingFields,
  composeManualBookingPhoneNumber,
  DEFAULT_MANUAL_BOOKING_COUNTRY_CODE,
  hasManualBookingClient,
  isManualBookingNewClient,
  MANUAL_BOOKING_NEW_CLIENT,
  resolveManualBookingClientUserId,
  resolveManualBookingShareUrl,
} from '../../utils/manualHostBooking';
import {
  parseBookingCalendarInstant,
  serializeBookingWallClockForApi,
} from '../../utils/bookingInstant';
import TutorBlockTimeDialog from './TutorBlockTimeDialog';
import TutorCalendarEventDetailsDialog from './TutorCalendarEventDetailsDialog';
import HostTutorWalkInDialog from '../host/HostTutorWalkInDialog';
import ManualBookingLinkDialog from '../host/ManualBookingLinkDialog';
import { tutorApiClient } from '../../tutorApiClient';

const TUTOR_CALENDAR_CORNER_RADIUS = '8px';
const TUTOR_CALENDAR_ACCENT = '#4E67EB';
const TUTOR_CALENDAR_DAY_DOT_LIMIT = 4;

export interface TutorCalendarTutorOption {
  tutorId: string;
  name: string;
  timezone?: string | null;
  sessionTypes?: string[];
}

function fullCalendarHeaderDayKey(date: Date): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

function pushDayHeaderDot(dotsByDay: Map<string, string[]>, start: string, color: string) {
  const day = start.slice(0, 10);
  const dots = dotsByDay.get(day) ?? [];
  if (dots.length >= TUTOR_CALENDAR_DAY_DOT_LIMIT) return;
  dots.push(color);
  dotsByDay.set(day, dots);
}

export interface TutorScheduleCalendarProps {
  mode: 'tutor' | 'host';
  title: string;
  emptyStateTitle: string;
  emptyStateHint: string;
  tutors: TutorCalendarTutorOption[];
  selectedTutor: TutorCalendarTutorOption | null;
  onTutorChange?: (tutorId: string) => void;
  weeklySchedule: SpaceWeeklySchedule | null;
  timezone: string;
  events: TutorCalendarLessonEvent[];
  loading?: boolean;
  onRangeChange: (start: Date, end: Date) => void;
  editSchedulePath: string;
  /** Create a manual calendar block (no tutor booking). */
  onCreateManualBlock?: (request: CreateTutorCalendarEventRequest) => Promise<void>;
  /** Delete a manual calendar block. Receives the calendar event id. */
  onDeleteManualBlock?: (calendarEventId: string) => Promise<void>;
  /** Host-only: create a confirmed walk-in / manual lesson. */
  onCreateWalkIn?: (
    request: CreateHostTutorBookingRequest,
  ) => Promise<CreateManualTutorBookingResponse | void>;
  walkInLessonOptions?: TutorLessonOption[];
  /** Organization single-booking discount shown on the manual lesson form. */
  walkInDiscountSettings?: DiscountSettings | null;
}

function parseIsoToLocalDate(isoString: string): Date {
  const [datePart, timePart] = isoString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  if (timePart) {
    const [hourStr, minuteStr] = timePart.split(':');
    return new Date(year, month - 1, day, Number(hourStr), Number(minuteStr), 0, 0);
  }
  return new Date(year, month - 1, day);
}

function combineDateTime(date: Date, time: Date): Date {
  const combined = new Date(date);
  combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return combined;
}

function formatTimeForApi(dateTime: Date, timezone: string): string {
  return serializeBookingWallClockForApi(dateTime, timezone);
}

export default function TutorScheduleCalendar({
  mode,
  title,
  emptyStateTitle,
  emptyStateHint,
  tutors,
  selectedTutor,
  onTutorChange,
  weeklySchedule,
  timezone,
  events,
  loading = false,
  onRangeChange,
  editSchedulePath,
  onCreateManualBlock,
  onDeleteManualBlock,
  onCreateWalkIn,
  walkInLessonOptions = [],
  walkInDiscountSettings = null,
}: TutorScheduleCalendarProps) {
  const { t } = useTranslation();
  const { mode: themeMode } = useThemeContext();
  const calendarRef = useRef<FullCalendar>(null);
  const calendarContainerRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<'dayGridMonth' | 'timeGridDay' | 'timeGridWeek'>('timeGridWeek');
  const [toolbarDate, setToolbarDate] = useState<Date>(() => new Date());
  const [visibleStart, setVisibleStart] = useState<Date | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<TutorCalendarLessonEvent | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteBlockDialogOpen, setDeleteBlockDialogOpen] = useState(false);
  const [deletingBlock, setDeletingBlock] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const canCreateBlocks = Boolean(onCreateManualBlock);
  const canDeleteBlocks = Boolean(onDeleteManualBlock);
  const canCreateWalkIn = Boolean(onCreateWalkIn);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockTitle, setBlockTitle] = useState('');
  const [blockDate, setBlockDate] = useState<Date | null>(null);
  const [blockTime, setBlockTime] = useState<Date | null>(null);
  const [blockDuration, setBlockDuration] = useState(60);
  const [submittingBlock, setSubmittingBlock] = useState(false);

  const [walkInDialogOpen, setWalkInDialogOpen] = useState(false);
  const [walkInDate, setWalkInDate] = useState<Date | null>(null);
  const [walkInTime, setWalkInTime] = useState<Date | null>(null);
  const [walkInExtraStarts, setWalkInExtraStarts] = useState<Date[]>([]);
  const [walkInLessonOptionId, setWalkInLessonOptionId] = useState('');
  const [walkInClientId, setWalkInClientId] = useState(MANUAL_BOOKING_NEW_CLIENT);
  const [walkInGuestPhoneLocal, setWalkInGuestPhoneLocal] = useState('');
  const [walkInSendSms, setWalkInSendSms] = useState(true);
  const [walkInClients, setWalkInClients] = useState<HostBookingClientOption[]>([]);
  const [walkInClientsLoading, setWalkInClientsLoading] = useState(false);
  const [submittingWalkIn, setSubmittingWalkIn] = useState(false);
  const [bookingLinkDialogOpen, setBookingLinkDialogOpen] = useState(false);
  const [createdBookingLinkUrl, setCreatedBookingLinkUrl] = useState<string | null>(null);
  const [bookingLinkCopied, setBookingLinkCopied] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const getFullCalendarLocale = (locale: string) => {
    const localeMap: Record<string, string> = { en: 'en', pl: 'pl' };
    return localeMap[locale] || 'en';
  };

  useEffect(() => {
    const updateCalendarSize = () => calendarRef.current?.getApi()?.updateSize();
    window.addEventListener('resize', updateCalendarSize);

    const container = calendarContainerRef.current;
    let resizeObserver: ResizeObserver | null = null;
    let debounceTimer: number | undefined;

    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        window.clearTimeout(debounceTimer);
        updateCalendarSize();
        debounceTimer = window.setTimeout(updateCalendarSize, 220);
      });
      resizeObserver.observe(container);
    }

    updateCalendarSize();
    return () => {
      window.removeEventListener('resize', updateCalendarSize);
      window.clearTimeout(debounceTimer);
      resizeObserver?.disconnect();
    };
  }, [events.length, selectedTutor?.tutorId]);

  useEffect(() => {
    calendarRef.current?.getApi()?.updateSize();
  }, [events.length]);

  const timeRange = useMemo(
    () => calculateScheduleTimeRange(weeklySchedule ?? undefined),
    [weeklySchedule],
  );

  const workingHoursEvents = useMemo(
    () => buildWorkingHoursOverlayEvents(weeklySchedule ?? undefined, visibleStart, timezone),
    [timezone, weeklySchedule, visibleStart],
  );

  const blockedSlotEvents = useMemo(
    () => buildBlockedSlotOverlayEvents(weeklySchedule ?? undefined, visibleStart, timezone),
    [timezone, weeklySchedule, visibleStart],
  );

  const dayOffEvents = useMemo(
    () => buildDayOffOverlayEvents(weeklySchedule ?? undefined, visibleStart, timezone),
    [timezone, weeklySchedule, visibleStart],
  );

  const pastBlockedEvents = useMemo(
    () => buildPastBlockedOverlayEvents(visibleStart, timezone),
    [timezone, visibleStart],
  );

  const formattedLessonEvents = useMemo(() => {
    if (!selectedTutor) return [];
    return events.map((event) => {
      const utcStart = parseBookingCalendarInstant(event.startTime);
      const utcEnd = parseBookingCalendarInstant(event.endTime);
      const color =
        event.kind === 'pending'
          ? TUTOR_CALENDAR_COLORS.pending
          : event.kind === 'external'
            ? TUTOR_CALENDAR_COLORS.external
            : event.kind === 'manual'
              ? TUTOR_CALENDAR_COLORS.manual
              : TUTOR_CALENDAR_COLORS.confirmed;

      const endWall = formatInTimeZone(utcEnd, timezone, "yyyy-MM-dd'T'HH:mm:ss");

      return {
        id: event.id,
        title: event.title,
        start: formatInTimeZone(utcStart, timezone, "yyyy-MM-dd'T'HH:mm:ss"),
        end: endWall,
        color,
        classNames: withPastEventClassNames(endWall, timezone, [`tutor-lesson-${event.kind}`]),
        allDay: false,
        extendedProps: { rawEvent: event },
      };
    });
  }, [events, selectedTutor, timezone]);

  const formattedEvents = useMemo(
    () => [
      ...workingHoursEvents,
      ...formattedLessonEvents,
      ...blockedSlotEvents,
      ...pastBlockedEvents,
      ...dayOffEvents,
    ],
    [
      workingHoursEvents,
      formattedLessonEvents,
      blockedSlotEvents,
      pastBlockedEvents,
      dayOffEvents,
    ],
  );

  const dayHeaderDots = useMemo(() => {
    const dotsByDay = new Map<string, string[]>();
    formattedLessonEvents.forEach((event) => {
      pushDayHeaderDot(dotsByDay, event.start, event.color);
    });
    return dotsByDay;
  }, [formattedLessonEvents]);

  const checkForConflicts = useCallback(
    (startUtc: Date, endUtc: Date) => {
      return events.some((event) => {
        const eventStart = parseBookingCalendarInstant(event.startTime);
        const eventEnd = parseBookingCalendarInstant(event.endTime);
        return startUtc < eventEnd && endUtc > eventStart;
      });
    },
    [events],
  );

  const blockStartDateTime =
    blockDate && blockTime ? combineDateTime(blockDate, blockTime) : null;
  const blockEndDateTime = blockStartDateTime
    ? new Date(blockStartDateTime.getTime() + blockDuration * 60 * 1000)
    : null;
  const blockStartUtc =
    blockStartDateTime && timezone
      ? zonedTimeToUtc(blockStartDateTime, timezone)
      : blockStartDateTime;
  const blockEndUtc =
    blockEndDateTime && timezone
      ? zonedTimeToUtc(blockEndDateTime, timezone)
      : blockEndDateTime;
  const isPastBlock = Boolean(blockStartUtc && blockStartUtc < new Date());
  const hasBlockConflict = Boolean(
    blockStartUtc && blockEndUtc && checkForConflicts(blockStartUtc, blockEndUtc),
  );

  const openBlockDialog = (date: Date | null = null, time: Date | null = null, duration = 60) => {
    setBlockTitle('');
    setBlockDate(date);
    setBlockTime(time);
    setBlockDuration(duration);
    setBlockDialogOpen(true);
  };

  const selectedWalkInOption = useMemo(
    () => walkInLessonOptions.find((option) => option.id === walkInLessonOptionId) ?? null,
    [walkInLessonOptions, walkInLessonOptionId],
  );
  const walkInDurationMinutes = selectedWalkInOption?.durationMinutes ?? 60;
  const walkInStartDateTime =
    walkInDate && walkInTime ? combineDateTime(walkInDate, walkInTime) : null;
  const walkInStarts = walkInStartDateTime ? [walkInStartDateTime, ...walkInExtraStarts] : [];
  const isPastWalkIn = walkInStarts.some((start) => {
    const utc = timezone ? zonedTimeToUtc(start, timezone) : start;
    return utc < new Date();
  });
  const hasWalkInConflict = walkInStarts.some((start) => {
    const utcStart = timezone ? zonedTimeToUtc(start, timezone) : start;
    const utcEnd = new Date(utcStart.getTime() + walkInDurationMinutes * 60 * 1000);
    return checkForConflicts(utcStart, utcEnd);
  });
  const walkInSessionsOverlap = bookingSessionsOverlap(
    walkInStarts.map((start) => ({ start, durationMinutes: walkInDurationMinutes })),
  );

  const openWalkInDialog = (date: Date | null = null, time: Date | null = null) => {
    const firstOptionId =
      walkInLessonOptions.find((option) => Boolean(option.id))?.id?.trim() || '';
    setWalkInDate(date);
    setWalkInTime(time);
    setWalkInExtraStarts([]);
    setWalkInLessonOptionId(firstOptionId);
    setWalkInClientId(MANUAL_BOOKING_NEW_CLIENT);
    setWalkInGuestPhoneLocal('');
    setWalkInSendSms(true);
    setWalkInClientsLoading(true);
    tutorApiClient
      .getTutorBookingClients()
      .then((clients) => setWalkInClients(clients))
      .catch(() => setWalkInClients([]))
      .finally(() => setWalkInClientsLoading(false));
    setWalkInDialogOpen(true);
  };

  const handleDatesSet = useCallback(
    (dateInfo: { start: Date; end: Date; view?: { type?: string; currentStart?: Date } }) => {
      setVisibleStart(dateInfo.start);
      const currentStart = dateInfo.view?.currentStart ?? dateInfo.start;
      setToolbarDate(currentStart instanceof Date ? currentStart : new Date(currentStart));
      const nextView = dateInfo.view?.type;
      if (nextView === 'dayGridMonth' || nextView === 'timeGridWeek' || nextView === 'timeGridDay') {
        setView(nextView);
      }
      onRangeChange(dateInfo.start, dateInfo.end);
    },
    [onRangeChange],
  );

  const toolbarMonthYearLabel = useMemo(
    () =>
      toolbarDate
        .toLocaleDateString(t('calendar.locale', 'en'), { month: 'long', year: 'numeric' })
        .replace(/^\w/, (c: string) => c.toUpperCase()),
    [t, toolbarDate],
  );

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.setOption('slotMinTime', timeRange.slotMinTime);
    api.setOption('slotMaxTime', timeRange.slotMaxTime);
    api.setOption('scrollTime', timeRange.slotMinTime);
    api.updateSize();
  }, [timeRange.slotMinTime, timeRange.slotMaxTime, selectedTutor?.tutorId]);

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.setOption('customButtons', {
      monthYearTitle: {
        text: toolbarMonthYearLabel,
        click: () => undefined,
      },
    });
  }, [toolbarMonthYearLabel, selectedTutor?.tutorId]);

  const handleEventClick = (clickInfo: EventClickArg) => {
    const raw = clickInfo.event.extendedProps?.rawEvent as TutorCalendarLessonEvent | undefined;
    if (raw) {
      setSelectedEvent(raw);
      setDetailsOpen(true);
    }
  };

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    if (!canCreateBlocks) return;
    const startLocal = parseIsoToLocalDate(selectInfo.startStr);
    const endLocal = parseIsoToLocalDate(selectInfo.endStr);
    if (startLocal < new Date()) {
      setSnackbarMessage(t('spaceCalendar.cannotSelectPastDate', 'Cannot select a date in the past'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      selectInfo.view.calendar.unselect();
      return;
    }
    const dateOnly = new Date(startLocal.getFullYear(), startLocal.getMonth(), startLocal.getDate());
    const durationMs = endLocal.getTime() - startLocal.getTime();
    const durationMinutes = Math.max(60, Math.round(durationMs / (1000 * 60)));
    openBlockDialog(dateOnly, startLocal, durationMinutes);
    selectInfo.view.calendar.unselect();
  };

  const handleDateClick = (dateInfo: { dateStr: string }) => {
    if (!canCreateBlocks) return;
    const preciseDateTime = parseIsoToLocalDate(dateInfo.dateStr);
    if (preciseDateTime < new Date()) {
      setSnackbarMessage(t('spaceCalendar.cannotSelectPastDate', 'Cannot select a date in the past'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    const dateOnly = new Date(
      preciseDateTime.getFullYear(),
      preciseDateTime.getMonth(),
      preciseDateTime.getDate(),
    );
    openBlockDialog(dateOnly, preciseDateTime, 60);
  };

  const handleCreateBlock = async () => {
    if (!onCreateManualBlock || !selectedTutor || !blockStartDateTime || !blockEndDateTime) {
      setSnackbarMessage(t('spaceCalendar.fillAllRequiredFields', 'Please fill all required fields'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (isPastBlock) {
      setSnackbarMessage(t('spaceCalendar.cannotBookInPast', 'Cannot book in the past'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (hasBlockConflict) {
      setSnackbarMessage(
        t('tutorCalendar.slotConflictWarning', 'This slot conflicts with an existing lesson or block.'),
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const titleValue =
      blockTitle.trim() || t('spaceCalendar.block', 'Block');
    const startTime = formatTimeForApi(blockStartDateTime, timezone);
    const endTime = formatTimeForApi(blockEndDateTime, timezone);

    setSubmittingBlock(true);
    try {
      await onCreateManualBlock({ title: titleValue, startTime, endTime });
      setSnackbarMessage(t('spaceCalendar.eventCreatedSuccess', 'Event created successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setBlockDialogOpen(false);
      setBlockTitle('');
    } catch (error) {
      console.error('Error creating tutor calendar event:', error);
      setSnackbarMessage(t('spaceCalendar.eventCreationError', 'Failed to create event'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSubmittingBlock(false);
    }
  };

  const handleCloseEventDetails = () => {
    setDetailsOpen(false);
    setSelectedEvent(null);
    setDeleteBlockDialogOpen(false);
  };

  const handleDeleteManualBlock = async () => {
    if (!onDeleteManualBlock || !selectedEvent || selectedEvent.kind !== 'manual') return;
    const calendarEventId = selectedEvent.calendarEventId?.trim();
    if (!calendarEventId) {
      setSnackbarMessage(t('tutorCalendar.deleteBlockError', 'Failed to delete block'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setDeletingBlock(true);
    try {
      await onDeleteManualBlock(calendarEventId);
      setDeleteBlockDialogOpen(false);
      handleCloseEventDetails();
      setSnackbarMessage(t('tutorCalendar.deleteBlockSuccess', 'Block deleted'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting tutor calendar event:', error);
      setSnackbarMessage(t('tutorCalendar.deleteBlockError', 'Failed to delete block'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeletingBlock(false);
    }
  };

  const handleCreateWalkIn = async () => {
    if (!onCreateWalkIn || !selectedTutor || !walkInStartDateTime || !selectedWalkInOption?.id) {
      setSnackbarMessage(t('spaceCalendar.fillAllRequiredFields', 'Please fill all required fields'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (isPastWalkIn) {
      setSnackbarMessage(t('spaceCalendar.cannotBookInPast', 'Cannot book in the past'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (hasWalkInConflict) {
      setSnackbarMessage(
        t('tutorCalendar.slotConflictWarning', 'This slot conflicts with an existing lesson or block.'),
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (walkInSessionsOverlap) {
      setSnackbarMessage(
        t('booking.sessionsOverlap', "These sessions overlap. Choose times that don't run into each other."),
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const wallStarts = [walkInStartDateTime, ...walkInExtraStarts];
    const occurrences = buildTutorBookingOccurrences(wallStarts, (start) => formatTimeForApi(start, timezone));
    const startTime = occurrences?.[0]?.startTime ?? formatTimeForApi(walkInStartDateTime, timezone);
    const userId = resolveManualBookingClientUserId(walkInClientId);
    const guestFields = buildManualGuestBookingFields({
      userId,
      phoneNumber: isManualBookingNewClient(walkInClientId)
        ? composeManualBookingPhoneNumber(DEFAULT_MANUAL_BOOKING_COUNTRY_CODE, walkInGuestPhoneLocal)
        : null,
      sendSms: walkInSendSms,
    });
    const validationError = validateHostTutorWalkInBooking({
      tutorId: selectedTutor.tutorId,
      lessonOptionId: selectedWalkInOption.id,
      startTime,
      sessionType: selectedWalkInOption.sessionType,
    });
    if (validationError) {
      setSnackbarMessage(t('spaceCalendar.fillAllRequiredFields', 'Please fill all required fields'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setSubmittingWalkIn(true);
    try {
      const response = await onCreateWalkIn(
        buildHostTutorWalkInBookingRequest({
          tutorId: selectedTutor.tutorId,
          lessonOptionId: selectedWalkInOption.id,
          startTime,
          occurrences,
          sessionType: selectedWalkInOption.sessionType,
          userId: guestFields.userId,
          phoneNumber: guestFields.phoneNumber,
          sendSms: guestFields.sendSms,
        }),
      );
      const shareUrl = occurrences
        ? null
        : resolveManualBookingShareUrl(response?.bookingLinkUrl);
      setSnackbarMessage(
        response && !hasManualBookingClient(response.userId)
          ? t(
              'spaceCalendar.manualBookingNoClientAttached',
              'Lesson booked. No client account was attached.',
            )
          : t('tutorCalendar.walkInCreatedSuccess', 'Walk-in lesson booked'),
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setWalkInDialogOpen(false);
      if (shareUrl) {
        setCreatedBookingLinkUrl(shareUrl);
        setBookingLinkCopied(false);
        setBookingLinkDialogOpen(true);
      }
    } catch (error) {
      console.error('Error creating walk-in tutor booking:', error);
      setSnackbarMessage(t('tutorCalendar.walkInCreatedError', 'Failed to book walk-in lesson'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSubmittingWalkIn(false);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart == null || touchEnd == null) return;
    const distance = touchStart - touchEnd;
    const api = calendarRef.current?.getApi();
    if (!api) return;
    if (distance > 50) api.next();
    else if (distance < -50) api.prev();
  };

  // App bar + fixed site footer clearance (see chatLayout / App Footer).
  const pageShellSx = {
    height: { xs: 'auto', md: 'calc(100dvh - 72px - 52px)' },
    minHeight: { xs: 'calc(100dvh - 56px - 52px)', md: 'calc(100dvh - 72px - 52px)' },
    display: 'flex',
    flexDirection: 'column',
    px: { xs: 1.5, sm: 2.5, lg: 3 },
    py: { xs: 1.5, md: 2 },
    boxSizing: 'border-box',
  };

  const calendarFrameSx = {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    bgcolor: 'transparent',
    border: 0,
    boxShadow: 'none',
    backgroundImage: 'none',
  };

  const hasTutors = tutors.length > 0;
  const showTutorPicker = tutors.length > 1 && Boolean(onTutorChange);
  const canSelectSlots = canCreateBlocks && view !== 'dayGridMonth';

  return (
    <Box sx={pageShellSx}>
      <Box
        sx={{
          ...calendarFrameSx,
          flex: 1,
          height: { xs: 'min(78vh, 720px)', md: '100%' },
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            pt: 1.25,
            pb: 1.25,
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
            {showTutorPicker ? (
              <FormControl size="small" sx={{ minWidth: 148, maxWidth: 220, flexShrink: 0 }}>
                <Select
                  aria-label={t('tutorCalendar.selectTutor', 'Tutor')}
                  value={selectedTutor?.tutorId ?? ''}
                  onChange={(event) => onTutorChange?.(event.target.value)}
                  sx={{
                    height: 40,
                    borderRadius: TUTOR_CALENDAR_CORNER_RADIUS,
                    '& .MuiOutlinedInput-notchedOutline': { borderRadius: TUTOR_CALENDAR_CORNER_RADIUS },
                  }}
                >
                  {tutors.map((tutor) => (
                    <MenuItem key={tutor.tutorId} value={tutor.tutorId}>
                      {tutor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography
                variant="h5"
                noWrap
                sx={{ fontWeight: 600, letterSpacing: '-0.02em', minWidth: 0, lineHeight: 1.2 }}
              >
                {title}
              </Typography>
            )}

            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 'auto', flexShrink: 0 }}>
              {canCreateWalkIn ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EventAvailableRoundedIcon />}
                  onClick={() => openWalkInDialog()}
                  disabled={!selectedTutor}
                  sx={{ borderRadius: TUTOR_CALENDAR_CORNER_RADIUS, height: 40 }}
                >
                  {t('spaceCalendar.createManualBooking', 'Create booking')}
                </Button>
              ) : null}
              {canCreateBlocks ? (
                <Tooltip title={t('spaceCalendar.createEvent', 'Block Time')}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={t('spaceCalendar.createEvent', 'Block Time')}
                      onClick={() => openBlockDialog()}
                      disabled={!selectedTutor}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: TUTOR_CALENDAR_CORNER_RADIUS,
                        width: 40,
                        height: 40,
                      }}
                    >
                      <BlockRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              {selectedTutor ? (
                <Tooltip title={t('tutorCalendar.editWorkingHours', 'Edit working hours')}>
                  <IconButton
                    component={RouterLink}
                    to={editSchedulePath}
                    size="small"
                    aria-label={t('tutorCalendar.editWorkingHours', 'Edit working hours')}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: TUTOR_CALENDAR_CORNER_RADIUS,
                      width: 40,
                      height: 40,
                      color: 'text.primary',
                    }}
                  >
                    <EditCalendarRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>
        </Box>

        {!hasTutors ? (
          <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', p: 4 }}>
            <Stack spacing={1} alignItems="center" textAlign="center">
              <Typography variant="h6">{emptyStateTitle}</Typography>
              <Typography variant="body2" color="text.secondary">
                {emptyStateHint}
              </Typography>
            </Stack>
          </Box>
        ) : selectedTutor ? (
          <Box
            ref={calendarContainerRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            data-theme={themeMode}
            className="tutor-lesson-calendar"
            sx={{
              flexGrow: 1,
              width: '100%',
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              px: { xs: 1, sm: 1.5 },
              pb: { xs: 1.5, sm: 2 },
              opacity: loading ? 0.72 : 1,
              transition: 'opacity 0.2s ease',
              ...tutorCalendarStyles,
              '& .fc': {
                width: '100% !important',
                height: '100% !important',
                borderRadius: `${TUTOR_CALENDAR_CORNER_RADIUS} !important`,
              },
              '& .fc-view-harness, & .fc-scrollgrid, & .fc-timegrid': {
                width: '100% !important',
              },
              '& .fc-col-header-cell-cushion': {
                backgroundColor: 'transparent !important',
                color: '#1f1f1f !important',
                borderBottom: '1px solid rgba(0, 0, 0, 0.08) !important',
              },
              '& .fc-col-header-cell:nth-child(1) .fc-col-header-cell-cushion': {
                backgroundColor: 'transparent !important',
                color: '#1f1f1f !important',
                borderBottom: '1px solid rgba(0, 0, 0, 0.08) !important',
              },
              '& .fc-timegrid-axis': {
                width: '50px !important',
                minWidth: '50px !important',
                maxWidth: '50px !important',
                backgroundColor: 'transparent !important',
                borderRight: '1px solid rgba(0, 0, 0, 0.08) !important',
              },
              '& .fc-timegrid-slot-label-cushion': {
                color: 'rgba(0, 0, 0, 0.55) !important',
              },
              '& .fc-monthYearTitle-button': {
                color: '#1f1f1f !important',
                background: 'transparent !important',
                backgroundColor: 'transparent !important',
                border: 'none !important',
                boxShadow: 'none !important',
                borderRadius: '0 !important',
                padding: '0 !important',
                minHeight: '0 !important',
              },
              '& .fc-button:hover:not(.fc-monthYearTitle-button)': {
                backgroundColor: 'rgba(78, 103, 235, 0.08) !important',
                borderColor: `${TUTOR_CALENDAR_ACCENT} !important`,
                color: `${TUTOR_CALENDAR_ACCENT} !important`,
              },
              '& .fc-button.fc-button-active': {
                backgroundColor: `${TUTOR_CALENDAR_ACCENT} !important`,
                borderColor: `${TUTOR_CALENDAR_ACCENT} !important`,
                color: '#ffffff !important',
              },
            }}
          >
            <style>{sharedCalendarCompactStyles}</style>
            <style>{calendarOverlayCompactStyles}</style>
            <style>{tutorCalendarCompactStyles}</style>
            <style>{`
              .fc-toolbar-title { display: none !important; }
              .tutor-lesson-calendar .fc-monthYearTitle-button {
                display: inline-flex !important;
                visibility: visible !important;
                opacity: 1 !important;
                color: #1f1f1f !important;
                font-size: 1.05rem !important;
                font-weight: 600 !important;
                text-transform: none !important;
                background: transparent !important;
                background-color: transparent !important;
                border: none !important;
                outline: none !important;
                box-shadow: none !important;
                border-radius: 0 !important;
                padding: 0 !important;
                min-height: 0 !important;
                pointer-events: none !important;
                cursor: default !important;
              }
              .fc .fc-timegrid-cols {
                z-index: 3 !important;
              }
              .fc .fc-timegrid-col {
                position: relative !important;
              }
              .fc .fc-timegrid-col:not(:last-child)::after {
                content: '' !important;
                position: absolute !important;
                top: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                width: 1px !important;
                background: rgba(0, 0, 0, 0.1) !important;
                pointer-events: none !important;
                z-index: 6 !important;
              }
              .fc .fc-col-header-cell {
                position: relative !important;
              }
              .fc .fc-col-header-cell:not(:last-child)::after {
                content: '' !important;
                position: absolute !important;
                top: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                width: 1px !important;
                background: rgba(0, 0, 0, 0.1) !important;
                pointer-events: none !important;
                z-index: 6 !important;
              }
              .fc-timegrid-axis,
              .fc-timegrid-axis-frame,
              .fc-timegrid-slot-label,
              .fc-timegrid-axis-cushion {
                border-right: none !important;
              }
              .tutor-lesson-calendar .fc {
                border-radius: ${TUTOR_CALENDAR_CORNER_RADIUS} !important;
              }
              .tutor-lesson-calendar .fc .fc-col-header-cell {
                height: auto !important;
                min-height: 48px !important;
                max-height: none !important;
                padding: 8px 4px 6px !important;
              }
              .tutor-lesson-calendar .fc .fc-col-header-cell-cushion {
                display: flex !important;
                justify-content: center !important;
                padding: 0 !important;
              }
              .tutor-cal-day {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 4px;
                line-height: 1.15;
              }
              .tutor-cal-day.is-today {
                color: ${TUTOR_CALENDAR_ACCENT};
                font-weight: 700;
              }
              .tutor-cal-dots {
                display: flex;
                gap: 3px;
                min-height: 6px;
                align-items: center;
                justify-content: center;
              }
              .tutor-cal-dots i {
                width: 6px;
                height: 6px;
                border-radius: 50%;
                display: block;
              }
              .tutor-lesson-calendar .fc .fc-timegrid-col-events {
                margin: 0 2px !important;
              }
              .tutor-lesson-calendar .fc-dayGridMonth-button,
              .tutor-lesson-calendar .fc-timeGridWeek-button,
              .tutor-lesson-calendar .fc-timeGridDay-button,
              .tutor-lesson-calendar .fc-prev-button,
              .tutor-lesson-calendar .fc-next-button,
              .tutor-lesson-calendar .fc-today-button {
                border: 1px solid rgba(31, 33, 41, 0.28) !important;
                background-color: #fff !important;
              }
              .tutor-lesson-calendar .fc-prev-button,
              .tutor-lesson-calendar .fc-next-button,
              .tutor-lesson-calendar .fc-today-button,
              .tutor-lesson-calendar .fc-prev-button .fc-icon,
              .tutor-lesson-calendar .fc-next-button .fc-icon,
              .tutor-lesson-calendar .fc-today-button:disabled {
                color: #1F2129 !important;
                opacity: 1 !important;
              }
              .tutor-lesson-calendar .fc-dayGridMonth-button.fc-button-active,
              .tutor-lesson-calendar .fc-timeGridWeek-button.fc-button-active,
              .tutor-lesson-calendar .fc-timeGridDay-button.fc-button-active {
                border-color: ${TUTOR_CALENDAR_ACCENT} !important;
                background-color: ${TUTOR_CALENDAR_ACCENT} !important;
                color: #fff !important;
              }
              .tutor-lesson-calendar .fc-event.tutor-lesson-confirmed,
              .tutor-lesson-calendar .fc-event.tutor-lesson-pending,
              .tutor-lesson-calendar .fc-event.tutor-lesson-manual,
              .tutor-lesson-calendar .fc-event.manual-block {
                box-shadow: none !important;
              }
            `}</style>
            <FullCalendar
              key={`tutor-cal-${selectedTutor.tutorId}-${timeRange.slotMinTime}-${timeRange.slotMaxTime}`}
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={view}
              firstDay={1}
              dayHeaders
              events={formattedEvents}
              headerToolbar={{
                left: 'dayGridMonth,timeGridWeek,timeGridDay',
                center: 'monthYearTitle',
                right: 'prev,next today',
              }}
              customButtons={{
                monthYearTitle: {
                  text: toolbarMonthYearLabel,
                  click: () => undefined,
                },
              }}
              buttonText={{
                today: t('calendar.today', 'Today'),
                month: t('calendar.month', 'Month'),
                week: t('calendar.week', 'Week'),
                day: t('calendar.day', 'Day'),
              }}
              navLinks
              titleFormat={{
                month: 'long',
                year: 'numeric',
              }}
              timeZone={timezone}
              nowIndicator
              displayEventTime
              eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
              slotMinTime={timeRange.slotMinTime}
              slotMaxTime={timeRange.slotMaxTime}
              scrollTime={timeRange.slotMinTime}
              slotDuration="00:30:00"
              views={{
                dayGridMonth: {
                  displayEventTime: true,
                  dayMaxEvents: 3,
                  fixedWeekCount: false,
                  dayHeaderFormat: { weekday: 'short' },
                },
                timeGridDay: {
                  displayEventTime: true,
                  slotDuration: '00:30:00',
                  slotMinTime: timeRange.slotMinTime,
                  slotMaxTime: timeRange.slotMaxTime,
                  dayHeaderFormat: { day: 'numeric', weekday: 'short' },
                  allDaySlot: false,
                },
                timeGridWeek: {
                  displayEventTime: true,
                  slotDuration: '00:30:00',
                  slotMinTime: timeRange.slotMinTime,
                  slotMaxTime: timeRange.slotMaxTime,
                  dayHeaderFormat: { day: 'numeric', weekday: 'short' },
                  allDaySlot: false,
                },
              }}
              allDaySlot={false}
              selectable={canSelectSlots}
              select={canSelectSlots ? handleDateSelect : undefined}
              dateClick={canCreateBlocks ? handleDateClick : undefined}
              selectMirror={canSelectSlots}
              unselectAuto={false}
              selectMinDistance={1}
              dayMaxEvents={false}
              locales={allLocales}
              locale={getFullCalendarLocale(t('calendar.locale', 'en'))}
              dayHeaderFormat={{ weekday: 'short', day: '2-digit' }}
              dayHeaderContent={(arg) => {
                const showDots = arg.view.type !== 'dayGridMonth';
                const dots = showDots ? dayHeaderDots.get(fullCalendarHeaderDayKey(arg.date)) ?? [] : [];
                return (
                  <span className={arg.isToday ? 'tutor-cal-day is-today' : 'tutor-cal-day'}>
                    <span>{arg.text}</span>
                    {showDots ? (
                      <span className="tutor-cal-dots" aria-hidden="true">
                        {dots.map((color, index) => (
                          <i key={`${color}-${index}`} style={{ backgroundColor: color }} />
                        ))}
                      </span>
                    ) : null}
                  </span>
                );
              }}
              expandRows
              handleWindowResize
              height="100%"
              datesSet={handleDatesSet}
              eventDidMount={(info) => mountPastEventClass(info, timezone)}
              eventClick={handleEventClick}
              editable={false}
              droppable={false}
              slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            />
          </Box>
        ) : null}
      </Box>

      <TutorCalendarEventDetailsDialog
        open={detailsOpen}
        event={selectedEvent}
        audience={mode === 'host' ? 'host' : 'tutor'}
        onClose={handleCloseEventDetails}
        canDeleteBlock={canDeleteBlocks}
        deletingBlock={deletingBlock}
        onRequestDeleteBlock={() => setDeleteBlockDialogOpen(true)}
        timezone={timezone}
      />

      <Dialog
        open={deleteBlockDialogOpen}
        onClose={() => !deletingBlock && setDeleteBlockDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('tutorCalendar.deleteBlockTitle', 'Delete this block?')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t(
              'tutorCalendar.deleteBlockConfirm',
              'This removes the manual block from the tutor calendar. This cannot be undone.',
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBlockDialogOpen(false)} disabled={deletingBlock}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() => {
              void handleDeleteManualBlock();
            }}
            color="error"
            variant="contained"
            disabled={deletingBlock}
          >
            {deletingBlock
              ? t('common.loading', 'Loading...')
              : t('tutorCalendar.deleteBlock', 'Delete block')}
          </Button>
        </DialogActions>
      </Dialog>

      {canCreateBlocks ? (
        <TutorBlockTimeDialog
          open={blockDialogOpen}
          onClose={() => setBlockDialogOpen(false)}
          title={blockTitle}
          setTitle={setBlockTitle}
          selectedDate={blockDate}
          setSelectedDate={setBlockDate}
          selectedTime={blockTime}
          setSelectedTime={setBlockTime}
          durationMinutes={blockDuration}
          setDurationMinutes={setBlockDuration}
          isPast={isPastBlock}
          hasConflict={hasBlockConflict}
          submitting={submittingBlock}
          onSubmit={() => {
            void handleCreateBlock();
          }}
        />
      ) : null}

      {canCreateWalkIn ? (
        <HostTutorWalkInDialog
          open={walkInDialogOpen}
          onClose={() => setWalkInDialogOpen(false)}
          lessonOptions={walkInLessonOptions}
          bookingClients={walkInClients}
          bookingClientsLoading={walkInClientsLoading}
          selectedClientId={walkInClientId}
          setSelectedClientId={setWalkInClientId}
          guestPhoneLocal={walkInGuestPhoneLocal}
          setGuestPhoneLocal={setWalkInGuestPhoneLocal}
          sendBookingLinkSms={walkInSendSms}
          setSendBookingLinkSms={setWalkInSendSms}
          selectedDate={walkInDate}
          setSelectedDate={setWalkInDate}
          selectedTime={walkInTime}
          setSelectedTime={setWalkInTime}
          extraSessionStarts={walkInExtraStarts}
          setExtraSessionStarts={setWalkInExtraStarts}
          lessonOptionId={walkInLessonOptionId}
          setLessonOptionId={setWalkInLessonOptionId}
          isPast={isPastWalkIn}
          hasConflict={hasWalkInConflict}
          submitting={submittingWalkIn}
          discountSettings={walkInDiscountSettings}
          onSubmit={() => {
            void handleCreateWalkIn();
          }}
        />
      ) : null}

      <ManualBookingLinkDialog
        open={bookingLinkDialogOpen}
        bookingLinkUrl={createdBookingLinkUrl}
        copied={bookingLinkCopied}
        onCopy={() => {
          if (!createdBookingLinkUrl) {
            return;
          }
          void navigator.clipboard.writeText(createdBookingLinkUrl).then(
            () => setBookingLinkCopied(true),
            (error) => console.error('Failed to copy booking link:', error),
          );
        }}
        onClose={() => setBookingLinkDialogOpen(false)}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
